import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  CAN_EXCHANGE,
  COMPANY_ID,
  DEFAULT_HAZMAT_FEE,
  ID,
  INVENTORY_ID,
  MIN_OUTRIGHT_VALUE_FOR_EXCHANGE_CONSIDERATION,
  QUANTITY,
  SERVICEABLE_TOOLING,
  SETTINGS
} from "APP_ROOT/constants";
import { Map } from "immutable";
import { properCase } from "LIB/formatters/text";
import { connect } from "react-redux";
import { Col, Row } from "react-bootstrap";
import _ from "lodash";
import {
  formValueSelector,
  Field,
  Fields,
  FormSection,
  change as reduxFormChange
} from "redux-form";
import DoNotDoBusinessWarning from "MODULES/companies/shared/components/DoNotDoBusinessWarning";
import DoNotQuoteWarning from "MODULES/companies/shared/components/DoNotQuoteWarning";
import {
  NoPmaNoDerWarning,
  TraceTypeWarning,
  StrictTraceWarning,
  WarrantyMinimumWarning
} from "MODULES/SalesOrders/components/ConvertFromCartForm/Warnings";
import api from "APP_ROOT/api";
import {
  Errors,
  form,
  Form as AppForm,
  FormRow,
  TextArea,
  Check,
  CheckGroup,
  FormInput,
  Select
} from "MODULES/shared/components/Forms";
import * as companyActions from "MODULES/companies/actions";
import PartDetails, {
  copyFromInventoryFieldsOnOQ,
  warrantyForCondition
} from "MODULES/OutgoingQuotes/shared/PartDetails";
import OutgoingQuoteTypeSelect from "MODULES/OutgoingQuotes/shared/OutgoingQuoteTypeSelect";
import warnings from "MODULES/OutgoingQuotes/shared/warnings";
import CompanyAddressWarning from "MODULES/shared/components/CompanyAddressWarning";
import FormToggle from "MODULES/companies/shared/FindOrCreateCompanyToggle/FormToggle";
import { isLessThan36HoursAgo, monthsAgo } from "LIB/time";
import SelectInventoryTable from "MODULES/OutgoingQuotes/shared/SelectInventoryTable";
import {
  AS_IS,
  ATTACH_TAG_COPIES,
  ATTACH_TRACEABILITY,
  EXCHANGE,
  EXCHANGE_FEE,
  EXCHANGE_ONLY,
  INVENTORY_IDS,
  NO_INTEREST,
  NO_QUOTE,
  OFFER,
  OUTGOING_LINE_ITEMS,
  PRICE_PER_UNIT,
  QUOTE_TYPE,
  SELL_AS_ALT_ID,
  STANDARD,
  WARRANTY,
  MAKE_OFFER,
  AOG,
  AUTO_FOLLOWUP_DAYS,
  AUTO_FOLLOWUP
} from "MODULES/OutgoingQuotes/constants";
import { handleRepairItem } from "MODULES/OutgoingQuotes/shared/helpers";
import { searchQuoteCompanies } from "MODULES/parts/actions";
import {
  AS_REMOVED,
  CONDITION,
  DO_NOT_LIST,
  EASA,
  FAA,
  UK_CAA,
  LIST,
  REPAIRING,
  SERIAL_NUMBER,
  TAG_COPIES,
  TAG_DATE,
  TAGGED_BY,
  TOTAL_CYCLES_REMAINING,
  TOTAL_HOURS_REMAINING,
  TRACE_TYPE,
  TRACEABILITY,
  TRACED_TO,
  WARRANTY_OVERRIDE
} from "MODULES/inventories/constants";
import { INTERNAL_NOTE } from "MODULES/shared/constants";
import { EXTERNAL_NOTE, HAZMAT_FEE } from "MODULES/PurchaseOrders/constants";
import {
  HAZMAT,
  LLP,
  PART,
  PASS_FAIL,
  PERMANENT_EXTERNAL_NOTE_FOR_OQ,
  TOOLING_PART_TYPE
} from "MODULES/parts/constants";
import { CORE_DUE_DAYS, REPAIR_AND_HOLD } from "MODULES/RepairOrders/constants";
import { BEYOND_ECON_REPAIR } from "MODULES/Receiving/constants";
import { SALES } from "MODULES/users/constants";
import { hasRole } from "MODULES/users/helpers";
import { LineItemsTable } from "MODULES/parts/components/Detail/OutgoingQuotes/LineItemsTable";
import SelectImages from "MODULES/parts/components/Detail/OutgoingQuotes/SelectImages";
import PartHazmatWarning from "MODULES/parts/components/shared/PartHazmatWarning";
import { decimalField } from "LIB/formatters/number";
import { DO_NOT_EXCHANGE } from "MODULES/companies/constants";
import DoNotExchangeWarning from "MODULES/companies/shared/components/DoNotExchangeWarning";
import NumberInput from "MODULES/shared/components/Forms/NumberInput/NumberInput";
import { NoteTooltip, Warning } from "MODULES/shared/components";
import { useSettings } from "APP_ROOT/hooks/useCurrentUser";
import { SEND_QUOTE } from "MODULES/IncomingQuotes/constants";
import validate from "./validate";
import styles from "../styles";

function within72BusinessHours(date) {
  if (!date) return false;
  const now = new Date();
  const created = new Date(date);

  let hoursPassed = 0;
  const dayMillis = 24 * 60 * 60 * 1000;
  let cursor = new Date(created);

  while (cursor < now) {
    const day = cursor.getDay(); // 0=Sun,1=Mon,...6=Sat
    // Add 24 hours only if weekday
    if (day !== 0 && day !== 6) {
      hoursPassed += 24;
    }
    cursor = new Date(cursor.getTime() + dayMillis);
    if (hoursPassed > 72) break;
  }
  return hoursPassed <= 72;
}

function AirlineEndUser72HrWarning({ recentQuotes, part, quoteType }) {
  if (![STANDARD, EXCHANGE_ONLY, AOG].includes(quoteType)) return null;

  const interestingTypes = [STANDARD, EXCHANGE_ONLY, AOG, EXCHANGE];
  const airlineQuotes = recentQuotes.filter(rq => {
    if (!rq.company) return false;
    const ct = rq.company.company_type;
    const typeMatches = interestingTypes.includes(rq.quote_type.toLowerCase());
    const isAirlineEndUser = ct === "airline" || ct === "end_user";
    return (
      typeMatches && isAirlineEndUser && within72BusinessHours(rq.created_at)
    );
  });

  if (!airlineQuotes.length) return null;

  // Show the first or most recent quote
  const lastOne = airlineQuotes[0];

  const partNumber = (part && part.get("part_number")) || "this part";
  const companyName = lastOne.company.name || "airline/end-user";
  const quotedByUser = lastOne.created_by_user
    ? `${lastOne.created_by_user.first_name} ${lastOne.created_by_user
        .last_name}`
    : "(Backend System)";
  const contactName = lastOne.contact
    ? lastOne.contact.name
    : "(Backend System)";
  const quotedDate = new Date(lastOne.created_at).toLocaleString();

  return (
    <Warning>
      {`The part number (${partNumber}) has been quoted to ${companyName} within the last 72 hours.`}
      <br />
      {`It was quoted by ${quotedByUser} to ${contactName} on ${quotedDate}.`}
      <br />
      {`If you think it might be for the same order, please reconsider quoting prior to discussing with ${quotedByUser}.`}
    </Warning>
  );
}

const formName = "partDetailNewOutgoingQuote";
const FORM_SECTION = "outgoing_quote_line_item";

const getNameFromSection = field => `${FORM_SECTION}.${field}`;

const TraceabilityWarning = ({ company }) => {
  if (!company) return null;
  if (!company.vendor_trace_preapproval_required) return null;

  return (
    <Warning>
      Warning: This customer is flagged for trace approval being required in
      advance. Please consider attaching traceabliity to quote.
    </Warning>
  );
};

export class Form extends Component {
  static propTypes = {
    getFieldWithSection: PropTypes.func.isRequired,
    submitting: PropTypes.bool,
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pricePerUnit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setFieldValue: PropTypes.func.isRequired,
    quoteType: PropTypes.string,
    part: PropTypes.instanceOf(Map),
    change: PropTypes.func,
    companyId: PropTypes.number,
    inventoryIds: PropTypes.arrayOf(PropTypes.number),
    setSubmittedWith: PropTypes.func.isRequired,
    close: PropTypes.func.isRequired,
    selectedCompany: PropTypes.instanceOf(Object).isRequired,
    setSelectedCompany: PropTypes.func.isRequired
  };

  constructor() {
    super();
    this.notifyForm = this.notifyForm.bind(this);
  }

  state = {
    companyToggleType: "existing",
    company: {},
    companyQuotes: [],
    contacts: [],
    recentQuotes: [],
    companyPartHistory: {},
    quantityChanged: false,
    inventoryPrice: null,
    invalidCompany: false,
    selectedInventories: [],
    showAttachTraceability: false,
    showAttachTagCopies: false
  };

  componentDidMount() {
    if (this.props.part) {
      return api.parts.outgoingQuoteLineItems
        .search(this.props.part.get(ID), { limit: 30 })
        .then(res => {
          this.setState({ recentQuotes: res.data.results });
        });
    }

    return null;
  }

  setWarrantyField = val => {
    this.props.change(getNameFromSection(WARRANTY), val);
  };

  getInventoriesFilter() {
    const { quoteType } = this.props;

    const inventoriesFilter = {
      limit: "10",
      sellable_status: [LIST, DO_NOT_LIST].join(",")
    };

    if (quoteType === OFFER) {
      inventoriesFilter.or_in_condition = BEYOND_ECON_REPAIR;
    }

    return inventoriesFilter;
  }

  setSending = () => {
    this.setState(
      {
        showAttachTraceability: this.state.selectedInventories.some(i =>
          i.attachments.some(a => a.attachment_type === TRACEABILITY)
        ),
        showAttachTagCopies: this.state.selectedInventories.some(
          i =>
            i.attachments.some(
              a =>
                a.attachment_type === TAG_COPIES &&
                (!i.last_exchange_received ||
                  new Date(a.created_at) > new Date(i.last_exchange_received))
            ) &&
            i.repair_status !== REPAIR_AND_HOLD &&
            i.status !== REPAIRING
        )
      },
      () => {
        this.props.change(
          ATTACH_TAG_COPIES,
          this.state.showAttachTagCopies &&
            this.state.selectedInventories.every(
              i => i.condition !== AS_REMOVED
            )
        );
      }
    );
  };

  notifyForm = company => {
    if (
      company &&
      (!company.primary_physical_address || !company.billing_address)
    ) {
      return this.setState({ invalidCompany: true, company });
    }
    return this.setState({ invalidCompany: false });
  };

  copyInventoryFields = inventory =>
    Object.keys(copyFromInventoryFieldsOnOQ).forEach(field =>
      this.props.change(
        getNameFromSection(field),
        copyFromInventoryFieldsOnOQ[field](
          inventory,
          this.props.quoteType,
          this.props.part.toJS(),
          this.props.canExchange &&
            !_.get(this.props.selectedCompany, DO_NOT_EXCHANGE) &&
            !inventory.do_not_exchange &&
            !inventory.do_not_exchange_due_to_outright_value
        )
      )
    );

  clearFieldsCopiedFromInventory = () => {
    Object.keys(copyFromInventoryFieldsOnOQ).forEach(field => {
      if (
        field === WARRANTY &&
        this.props.quoteType === AS_IS &&
        this.state.inventoryIds &&
        this.state.inventoryIds.length <= 1
      ) {
        this.props.change(
          getNameFromSection(field),
          "No Warranty, unit sold as-is."
        );
      } else {
        this.props.change(getNameFromSection(field), null);
      }
    });

    [FAA, EASA, UK_CAA].forEach(f =>
      this.props.change(getNameFromSection(f), true)
    );
  };

  handleCompanySelected = company => {
    this.props.setSelectedCompany(company);

    if (company.do_not_quote) {
      this.props.setFieldValue(getNameFromSection(QUOTE_TYPE))(MAKE_OFFER);
    }

    if (company[DO_NOT_EXCHANGE]) {
      this.props.setFieldValue(getNameFromSection(EXCHANGE_FEE))(null);
    }

    const partId = this.props.part.get(ID);

    api.companies.parts.history(company.id, [partId]).then(({ data }) => {
      this.setState({
        companyPartHistory: data.results[0] || {},
        company
      });
    });

    api.parts.outgoingQuoteLineItems
      .search(partId, {
        limit: 5,
        company_id: company.id,
        created_after: monthsAgo(18)
      })
      .then(res => {
        this.setState({ companyQuotes: res.data.results });
      });
  };

  handleInventorySelected = inventory => {
    const { inventoryIds } = this.props;
    const { selectedInventories } = this.state;

    if (inventory === null) {
      this.props.change(getNameFromSection(INVENTORY_IDS), []);
      this.clearFieldsCopiedFromInventory();
      this.setState(
        {
          selectedInventories: []
        },
        () => {
          this.setSending();
        }
      );
      return;
    }

    if (inventoryIds.includes(inventory.id)) {
      const index = inventoryIds.indexOf(inventory.id);
      inventoryIds.splice(index, 1);
      this.props.change([FORM_SECTION, INVENTORY_IDS].join("."), inventoryIds);
      this.setState(
        {
          selectedInventories: selectedInventories.filter(
            i => i.id !== inventory.id
          )
        },
        () => {
          this.setSending();
        }
      );

      if (inventoryIds.length === 0) {
        this.clearFieldsCopiedFromInventory();
      }
    } else {
      inventoryIds.push(inventory.id);
      this.props.change([FORM_SECTION, INVENTORY_IDS].join("."), inventoryIds);
      this.setState(
        {
          selectedInventories: selectedInventories.concat([inventory])
        },
        () => {
          this.setSending();
        }
      );

      if (this.state.inventory !== inventory && inventoryIds.length === 1) {
        this.copyInventoryFields(inventory);
        api.inventories.show(inventory.id).then(res => {
          this.setState({ inventoryDetails: res.data }, () =>
            handleRepairItem({
              inventoryDetails: res.data,
              change: this.props.change,
              getNameFromSection
            })
          );
        });
      }

      if (inventoryIds.length > 1) {
        this.clearFieldsCopiedFromInventory();
      }
    }

    this.setState({
      quantityChanged: false,
      inventoryPrice: inventory && inventory.suggested_retail
    });
  };

  handleQuantityChanged = () => {
    if (!this.state.quantityChanged)
      this.props.change(getNameFromSection(SERIAL_NUMBER), "");
    this.setState({ quantityChanged: true });
  };

  handleEmail = email => {
    if (email !== "Add") {
      this.props.change("to", email);
    }
  };

  render() {
    const {
      canExchange,
      currentUser,
      getField,
      submitting,
      companyId,
      getFieldWithSection,
      inventoryIds,
      part,
      quoteType,
      warranty,
      setSubmittedWith,
      submitFailed,
      errorMessages,
      close,
      change,
      resetFields,
      selectedCompany,
      setFieldValue
    } = this.props;

    const {
      inventoryPrice,
      company,
      companyQuotes,
      invalidCompany,
      companyPartHistory,
      recentQuotes,
      selectedInventories,
      showAttachTraceability,
      showAttachTagCopies
    } = this.state;

    const mostRecentQuotes = recentQuotes.filter(
      quote =>
        isLessThan36HoursAgo(quote.created_at) && companyId === quote.company_id
    );

    const showSendButton = !inventoryIds || inventoryIds.length > 1;

    const showSendQuoteCheck = quoteType !== OFFER;
    const canSendQuote = showSendQuoteCheck && hasRole(currentUser, SALES);

    const showSendQuoteForm =
      (!inventoryIds || inventoryIds.length < 2) && canSendQuote;
    /* eslint-disable */
    let { exchange, ...filteredQuoteTypes } = OUTGOING_LINE_ITEMS.quoteType;
    /* eslint-enable */
    filteredQuoteTypes = part.get(PASS_FAIL)
      ? _.omit(filteredQuoteTypes, [EXCHANGE_ONLY])
      : filteredQuoteTypes;

    const altOptions = part
      .get("alternates")
      .toJS()
      .map(a => ({
        value: a.id,
        text: a.name
      }));

    const passFail = part.get(PASS_FAIL);

    const inventoriesFilter = this.getInventoriesFilter();
    const partObject = part.toJS();
    const isBERMaterialSelected = _.some(
      selectedInventories,
      item => item.condition === BEYOND_ECON_REPAIR
    );

    const minOutrightVal = useSettings()[
      MIN_OUTRIGHT_VALUE_FOR_EXCHANGE_CONSIDERATION
    ];

    const disablePartFields = {
      price_per_unit:
        isBERMaterialSelected &&
        getField(`${FORM_SECTION}.${OFFER}`) === NO_INTEREST,
      exchange_fee:
        isBERMaterialSelected ||
        (selectedCompany && selectedCompany[DO_NOT_EXCHANGE]) ||
        (minOutrightVal &&
          Number(minOutrightVal) > getField(`${FORM_SECTION}.price_per_unit`))
    };

    return (
      <div className="panel panel-default form-panel" style={styles.container}>
        <div className="panel-body">
          <button className="close" type="button" onClick={close}>
            <span>
              <i className="fa fa-close" />
            </span>
          </button>
          <h4 className="modal-title" style={styles.title}>
            Create a New Outgoing Quote
          </h4>
          <AppForm
            {...this.props}
            editable={!invalidCompany}
            submitting={submitting}
            modal={false}
          >
            <AppForm.Body>
              <AirlineEndUser72HrWarning
                recentQuotes={recentQuotes}
                part={part}
                quoteType={quoteType}
              />

              {passFail && (
                <Warning>
                  Due to the Pass / Fail nature of this item. Exchange Only is
                  not available as a Quote Type.
                </Warning>
              )}
              <DoNotQuoteWarning company={selectedCompany} />
              <DoNotDoBusinessWarning company={selectedCompany} />
              {warnings(mostRecentQuotes, companyPartHistory)}
              <NoPmaNoDerWarning
                inventories={selectedInventories}
                company={company}
              />
              <TraceTypeWarning
                inventories={selectedInventories}
                company={company}
              />
              <StrictTraceWarning
                inventories={selectedInventories}
                company={company}
              />
              <WarrantyMinimumWarning
                company={company}
                lineItems={[{ warranty }]}
              />
              <div className="row">
                <Col md={12}>
                  <FormRow style={{ marginTop: "26px" }}>
                    <Col md={6}>
                      <FormSection name={FORM_SECTION}>
                        <Fields
                          change={change}
                          fieldName={QUOTE_TYPE}
                          names={[
                            CONDITION,
                            INVENTORY_ID,
                            PRICE_PER_UNIT,
                            QUANTITY,
                            TAGGED_BY,
                            TAG_DATE,
                            TRACE_TYPE,
                            TRACED_TO,
                            EXCHANGE_FEE,
                            WARRANTY
                          ]}
                          component={OutgoingQuoteTypeSelect}
                          quoteTypes={filteredQuoteTypes}
                          price={inventoryPrice}
                        />
                      </FormSection>
                    </Col>
                  </FormRow>
                  <hr className="hr-xs hr-dashed" />
                  {invalidCompany && (
                    <CompanyAddressWarning company={company} />
                  )}
                  <DoNotExchangeWarning company={company} />
                  <FormToggle
                    formName={formName}
                    type="outgoingQuote"
                    companySearch={searchQuoteCompanies}
                    handleEmail={this.handleEmail}
                    onCompanySelected={this.handleCompanySelected}
                    setPrimaryContact={false}
                  />
                  {companyQuotes &&
                    companyQuotes.length > 0 && [
                      <h4 className="margin-bottom-30 line-height-2">
                        Most Recent Quotes
                      </h4>,
                      <div className="reset-panel-table">
                        <LineItemsTable
                          results={companyQuotes}
                          limitResults
                          loading={false}
                          inForm
                        />
                      </div>
                    ]}
                  <hr className="hr-sm" />
                  <h4>Select From Inventory</h4>
                  <div className="form-group">
                    <FormSection name={FORM_SECTION}>
                      <SelectInventoryTable
                        key={quoteType}
                        filter={inventoriesFilter}
                        part={partObject}
                        inventoryIds={inventoryIds}
                        onInventorySelected={this.handleInventorySelected}
                        modal={false}
                        change={change}
                        quoteType={quoteType}
                      />
                    </FormSection>
                  </div>
                  {selectedInventories.length === 1 &&
                    _.get(selectedInventories, "0.condition") !==
                      getFieldWithSection(CONDITION) && (
                      <Warning>
                        SRP Condition does not match actual condition.
                      </Warning>
                    )}
                  {quoteType !== NO_QUOTE && (
                    <div>
                      <hr className="hr-sm" />
                      <h4>Part Details</h4>
                      <FormSection name={FORM_SECTION}>
                        <PartDetails
                          canExchange={canExchange}
                          disableTagType={
                            inventoryIds && inventoryIds.length === 1
                          }
                          disabled={disablePartFields}
                          formName={formName}
                          formSection={FORM_SECTION}
                          getFieldValue={getFieldWithSection}
                          handleQuantityChanged={this.handleQuantityChanged}
                          inventoryIds={inventoryIds}
                          setWarrantyField={this.setWarrantyField}
                          part={partObject}
                          passFail={part.get(PASS_FAIL)}
                          quoteType={quoteType}
                          setFieldValue={setFieldValue}
                          selectedInventories={selectedInventories}
                          resetFields={() => resetFields(change)}
                          doNotExchangeInventoryLineItem={
                            selectedInventories.length > 0 &&
                            selectedInventories.some(i => i.do_not_exchange)
                          }
                        >
                          <PartHazmatWarning
                            hazmat={part.get(HAZMAT)}
                            hazmatFee={getFieldWithSection(HAZMAT_FEE)}
                          />
                          <FormRow>
                            <Col md={4}>
                              <div className="form-group">
                                <Field
                                  name={SELL_AS_ALT_ID}
                                  component={Select}
                                  label="Quote As Alt"
                                  options={altOptions}
                                />
                              </div>
                            </Col>
                            {part.get(LLP) && [
                              getFieldWithSection(TOTAL_HOURS_REMAINING) && (
                                <Col md={4}>
                                  <Field
                                    component={NumberInput}
                                    roundDigits={0}
                                    name={TOTAL_HOURS_REMAINING}
                                    label={properCase(TOTAL_HOURS_REMAINING)}
                                    disabled
                                  />
                                </Col>
                              ),
                              getFieldWithSection(TOTAL_CYCLES_REMAINING) && (
                                <Col md={4}>
                                  <Field
                                    component={NumberInput}
                                    roundDigits={0}
                                    name={TOTAL_CYCLES_REMAINING}
                                    label={properCase(TOTAL_CYCLES_REMAINING)}
                                    disabled
                                  />
                                </Col>
                              )
                            ]}
                            {part.get(HAZMAT) && (
                              <Col md={4}>
                                <div className="form-group">
                                  <Field
                                    name={HAZMAT_FEE}
                                    label={properCase(HAZMAT_FEE)}
                                    component={FormInput}
                                    type="number"
                                    step={0.01}
                                    onBlur={(ev, val) => {
                                      ev.preventDefault();
                                      setFieldValue(
                                        getNameFromSection(HAZMAT_FEE)
                                      )(decimalField(val));
                                    }}
                                  />
                                </div>
                              </Col>
                            )}
                            {quoteType === EXCHANGE_ONLY && (
                              <Col md={4}>
                                <Field
                                  label={properCase(CORE_DUE_DAYS)}
                                  name={CORE_DUE_DAYS}
                                  type="number"
                                  step={1}
                                  component={FormInput}
                                />
                              </Col>
                            )}
                          </FormRow>
                        </PartDetails>
                      </FormSection>
                      <hr className="hr-xs hr-dashed" />{" "}
                    </div>
                  )}
                  <div className="form-group">
                    <FormSection name={FORM_SECTION}>
                      <FormRow>
                        <Col md={6}>
                          <Field
                            name={INTERNAL_NOTE}
                            rows={5}
                            cols={50}
                            component={TextArea}
                            label="Internal Note (Applied to the line item)"
                          />
                        </Col>
                        <Col md={6}>
                          <Field
                            name={EXTERNAL_NOTE}
                            rows={5}
                            cols={50}
                            component={TextArea}
                            label="External Note (*visible to Customer*)"
                          />
                        </Col>
                      </FormRow>{" "}
                    </FormSection>{" "}
                  </div>
                </Col>
              </div>
              {showSendQuoteForm && (
                <div>
                  <TraceabilityWarning company={selectedCompany} />
                  <hr className="hr-sm" />
                  <div className="well">
                    <Row>
                      <Col md={6}>
                        <h4 style={{ marginTop: 0 }}>Send Quote</h4>
                        <Field
                          component={FormInput}
                          name="to"
                          label="To:"
                          disabled={!canSendQuote}
                        />
                        <Field
                          component={FormInput}
                          name="cc"
                          label="CC:"
                          disabled={!canSendQuote}
                        />
                        <Field
                          component={FormInput}
                          name="bcc"
                          label="BCC:"
                          disabled={!canSendQuote}
                        />
                      </Col>
                      <Col md={6}>
                        <div style={{ marginTop: "33px" }}>
                          {showAttachTraceability &&
                            ![NO_QUOTE, OFFER].includes(quoteType) && (
                              <CheckGroup
                                checkGroupStyles={{ marginBottom: 0 }}
                                justified
                              >
                                <Field
                                  name={ATTACH_TRACEABILITY}
                                  component={Check}
                                  label={properCase(ATTACH_TRACEABILITY)}
                                />
                              </CheckGroup>
                            )}
                          {showAttachTagCopies &&
                            ![NO_QUOTE, OFFER].includes(quoteType) && (
                              <CheckGroup
                                checkGroupStyles={{ marginBottom: 0 }}
                                justified
                              >
                                <Field
                                  name={ATTACH_TAG_COPIES}
                                  component={Check}
                                  label={properCase(ATTACH_TAG_COPIES)}
                                />
                              </CheckGroup>
                            )}
                          {showSendQuoteCheck && [
                            <CheckGroup
                              checkGroupStyles={{ marginBottom: 0 }}
                              justified
                            >
                              <Field
                                name={SEND_QUOTE}
                                component={Check}
                                label="Send Quote"
                                onChange={(_ev, val) => {
                                  if (!val) {
                                    setFieldValue(AUTO_FOLLOWUP)(false);
                                    setFieldValue(AUTO_FOLLOWUP_DAYS)(null);
                                  }
                                }}
                              />
                            </CheckGroup>,
                            getField(SEND_QUOTE) && (
                              <FormRow>
                                <Col md={6}>
                                  <CheckGroup
                                    checkGroupStyles={{ marginBottom: 0 }}
                                    justified
                                  >
                                    <Field
                                      name={AUTO_FOLLOWUP}
                                      component={Check}
                                      label="Auto Follow Up"
                                    />
                                  </CheckGroup>
                                </Col>
                                <Col md={6}>
                                  <Field
                                    label={[
                                      "# of Days ",
                                      <NoteTooltip
                                        placement="right"
                                        icon="fa-info-circle"
                                        color="rgba(255, 255, 255, 0.25)"
                                        note={"Range: 1 to 14"}
                                      />
                                    ]}
                                    name={AUTO_FOLLOWUP_DAYS}
                                    disabled={!getField(AUTO_FOLLOWUP)}
                                    component={NumberInput}
                                    roundDigits={0}
                                    type="number"
                                  />
                                </Col>
                              </FormRow>
                            )
                          ]}
                        </div>
                      </Col>
                    </Row>
                    {selectedInventories.length === 1 && (
                      <SelectImages
                        formSection={FORM_SECTION}
                        formName={formName}
                        inventory={selectedInventories[0]}
                      />
                    )}
                  </div>
                </div>
              )}
            </AppForm.Body>
            <AppForm.Footer>
              {submitFailed && <Errors errors={errorMessages} />}
              <AppForm.Cancel close={close} editable />
              <AppForm.Submit
                disabled={false}
                editable
                submitting={submitting}
                onClick={() => setSubmittedWith("save")}
              />
              <AppForm.Submit
                disabled={false}
                editable
                text="Save and Go To Quote"
                submitting={submitting}
                onClick={() => setSubmittedWith("save-and-go")}
              />
              {showSendButton && (
                <AppForm.Submit
                  disabled={canSendQuote}
                  editable
                  text="Send"
                  submitting={submitting}
                  onClick={() => setSubmittedWith("send")}
                />
              )}
            </AppForm.Footer>
          </AppForm>
        </div>
      </div>
    );
  }
}

const selector = formValueSelector(formName);

export default connect(
  (state, props) => ({
    initialValues: {
      bcc: props.currentUser.email,
      attach_tag_copies: false,
      show_hazmat_warning: state.partDetail.getIn([PART, HAZMAT]),
      outgoing_quote_line_item: {
        inventory_attachment_ids: [],
        cost: "",
        condition: state.partDetail.getIn([PART, TOOLING_PART_TYPE])
          ? SERVICEABLE_TOOLING
          : undefined,
        hazmat_fee: state.partDetail.getIn([PART, HAZMAT])
          ? state.session.getIn([SETTINGS, DEFAULT_HAZMAT_FEE])
          : undefined,
        inventory_ids: [],
        include_dom: false,
        quote_type: STANDARD,
        faa: true,
        easa: true,
        uk_caa: true,
        external_note: state.partDetail.getIn([
          PART,
          PERMANENT_EXTERNAL_NOTE_FOR_OQ
        ]),
        warranty:
          warrantyForCondition(null, state.partDetail.get(PART).toJS()) ||
          state.partDetail.getIn([PART, WARRANTY_OVERRIDE])
      }
    },
    canExchange: state.session.getIn([SETTINGS, CAN_EXCHANGE]),
    companyId: selector(state, COMPANY_ID),
    getFieldWithSection: field => selector(state, `${FORM_SECTION}.${field}`),
    inventoryIds: selector(state, getNameFromSection(INVENTORY_IDS)),
    getField: name => selector(state, name),
    part: state.partDetail.get(PART),
    pricePerUnit: selector(state, getNameFromSection(PRICE_PER_UNIT)),
    quantity: selector(state, getNameFromSection(QUANTITY)),
    quoteType: selector(state, getNameFromSection(QUOTE_TYPE)),
    warranty: selector(state, getNameFromSection(WARRANTY))
  }),
  dispatch => ({
    resetFields: change => {
      dispatch(change(getNameFromSection(PRICE_PER_UNIT), ""));
      dispatch(change(getNameFromSection(EXCHANGE_FEE), ""));
    },
    setFieldValue: field => value =>
      dispatch(reduxFormChange(formName, field, value))
  })
)(
  form({
    form: formName,
    validate,
    asyncValidate: companyActions.validateContact,
    asyncBlurFields: ["contact.email"]
  })(Form)
);



// WEBPACK FOOTER //
// ./app/modules/parts/components/Detail/OutgoingQuotes/Form.jsx